import type { PageSize, TableId } from '@myrewards/contracts';
import type { RefObject } from 'react';
import { usePaging } from '../ui';

export interface AdminPaging {
  page: number;
  size: PageSize;
  /** The page on its way while the router loads it (see `Paging.loading`); null otherwise. */
  loading: number | null;
  /** The size is settled: safe to fetch the list. */
  ready: boolean;
  /** On the table's wrapper (tabIndex -1): focus and scroll land here after a page change. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * `quiet` is for the screen's own corrections: no focus move, no scroll. `also` sets (or, with
   * null, drops) other params in the same navigation.
   */
  goTo: (n: number, opts?: { quiet?: boolean; also?: Record<string, string | null> }) => void;
  /** Rows per page; `firstRowIndex` (0-based) keeps the row at the top in view. */
  changeSize: (size: PageSize, firstRowIndex: number) => void;
}

/**
 * `usePaging` for a System administration list, as plain values and functions (so a page can use
 * them without handling the hook's object whole). `setPage` and `setSize` are stable callbacks.
 */
export function useAdminPaging(table: TableId): AdminPaging {
  const { page, size, loading, ready, anchorRef, setPage, setSize } = usePaging(table);
  return { page, size, loading, ready, anchorRef, goTo: setPage, changeSize: setSize };
}
